import React, {Fragment, useEffect, useState} from 'react';
import {$$theme} from '../../services/ThemeService/model.ts';
import {getEmbedDashboardUrl} from '../../services/BiService';
import {getDashboardUrl} from '../../services/BiService/BiUtils';
import {useUnit} from 'effector-react';
import {Box, CircularProgress} from '@mui/material';
import {Alert, Link} from '@ghs/components';
import {$$contactService} from '../../services/ContactService/model';

type BiDashboardEmbedProps = {
  workbookId: string;
  bookmarkId: string;
};

export default function BiDashboardEmbed({ workbookId, bookmarkId }: BiDashboardEmbedProps) {
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState<string | undefined>();
  const [error, setError] = useState(false);
  const selectedTheme = useUnit($$theme.$theme);
  const contactInfo = useUnit($$contactService.$contactInfo);
  const [currentExploreKey, setCurrentExploreKey] = useState<string | undefined>();
  const [currentBookmarkId, setCurrentBookmarkId] = useState<string | undefined>();

  useEffect(() => {
    async function getEmbedUrl() {
      setLoading(true);
      setError(false);

      let retries = 4;
      while (retries > 0) {
        try {
          setUrl(await getEmbedDashboardUrl(workbookId, bookmarkId, selectedTheme));
          setLoading(false);
          return;
        } catch (_) {
          retries--;
          if (retries === 0) {
            setError(true);
            setLoading(false);
          } else {
            await new Promise(resolve => setTimeout(resolve, 3000));
          }
        }
      }
    }

    getEmbedUrl();
  }, [workbookId, bookmarkId, selectedTheme]);

  useEffect(() => {
    // Need to listen to this event to resize the iframe when the browser window is resized
    // More info: https://help.sigmacomputing.com/docs/inbound-and-outbound-events-in-embeds#workbookpageheightonchange

    function adjustIframeHeight(iframe: HTMLIFrameElement, height: number) {
      // If adjustedHeight is the same (or very close) to the current height, the browser might ignore the update. Reset the size first
      iframe.style.height = '0px';

      // Update with the new height
      iframe.style.height = `${height}px`;

      // A CSS rule (e.g. !important, transition, or animation) may block the height change.
      iframe.style.setProperty('height', `${height}px`, 'important');
    }

    function sendSharingLinks(iframe: HTMLIFrameElement, selectedExploreKey?: string, selectedBookmarkId?: string) {
      // Send sharing link metadata to Sigma via postMessage

      const sharingLink = getDashboardUrl(workbookId, {
        bookmarkId: selectedBookmarkId,
        absolute: true
      });
      const sharingExplorationLink = selectedExploreKey
        ? getDashboardUrl(workbookId, {
            exploreKey: selectedExploreKey,
            bookmarkId: selectedBookmarkId,
            absolute: true
          })
        : null;

      console.log('[sendSharingLinks] Sharing Link:', sharingLink);

      if (iframe && iframe.contentWindow) {
        const iframeMessage = {
          type: 'workbook:sharinglink:update',
          sharingLink,
          sharingExplorationLink
        };
        iframe.contentWindow.postMessage(iframeMessage, import.meta.env.VITE_BI_SERVER_URL);
      }
    }

    function handleMessage(event: MessageEvent) {
      if (event.origin !== import.meta.env.VITE_BI_SERVER_URL) {
        // Only allow messages from trusted origins (In this case, only Sigma server)
        return;
      }

      const eventData = event.data;
      const iframe = document.getElementById(`sigma-embed-${workbookId}`) as HTMLIFrameElement | null;

      if (eventData.type === 'workbook:pageheight:onchange') {
        if (iframe && eventData.pageHeight) {
          // Sometimes layout changes don’t trigger repaint immediately. Read 'offsetHeight' to trigger reflow
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          iframe.offsetHeight;

          adjustIframeHeight(iframe, eventData.pageHeight);

          // If the iframe isn't fully rendered or layout isn't stable, the height change might be ignored or delayed.
          setTimeout(() => {
            adjustIframeHeight(iframe, eventData.pageHeight);
          }, 50);
        }
      }

      if (eventData.type === 'workbook:exploreKey:onchange') {
        setCurrentExploreKey(eventData.exploreKey);
        console.log('[listener] Explore Key Updated:', eventData.exploreKey);
      }

      if (eventData.type === 'workbook:bookmark:onchange') {
        setCurrentBookmarkId(eventData.bookmarkId);
        console.log('[listener] Bookmark ID Updated:', eventData.bookmarkId);
      }

      if (eventData.type === 'workbook:exploreKey:onchange' || eventData.type === 'workbook:bookmark:onchange' || eventData.type === 'workbook:loaded' || eventData.type === 'workbook:dataloaded') {
        // Use the latest values - either from event data or current state. This is to fix the React pitfall when using
        // currentBookmarkId and currentExploreKey as state variables (They may be not set yet when setCurrentExploreKey
        // and setCurrentBookmarkId are called above and then they are used here)
        const latestExploreKey = eventData.type === 'workbook:exploreKey:onchange' ? eventData.exploreKey : currentExploreKey;
        const latestBookmarkId = eventData.type === 'workbook:bookmark:onchange' ? eventData.bookmarkId : currentBookmarkId;

        sendSharingLinks(iframe, latestExploreKey, latestBookmarkId);
      }
    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [workbookId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" mt={1}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box sx={{ maxWidth: 'md' }}>
          <Alert id="failed-to-load-dashboard-msg" severity="error" sx={{ mb: 2, ml: 3, mr: 3 }} data-testid="failed-to-load-dashboard-alert">
            <Fragment>
              Failed to load the dashboard. Please try again later. If the error persists, please reach out to{' '}
              {contactInfo?.email ? <Link href={`mailto:${contactInfo.email}`}>{contactInfo.email}</Link> : 'your tracking supplier'} and provide your email address that was used, along with the
              date/time of the instance. We apologize for the inconvenience.
            </Fragment>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        overflow: 'hidden' // Prevent scrolling on the parent page, that is, prevent double scrolling
      }}
    >
      <iframe
        data-testid="sigma-embed-iframe"
        id={`sigma-embed-${workbookId}`}
        src={url}
        style={{
          height: '100vh',
          width: '100%',
          border: 'none',
          display: 'block',
          overflow: 'auto' // Allow content inside the iframe to scroll if it exceeds the iframe's height.
        }}
        title="dashboard"
      />
    </div>
  );
}
